import React from 'react';
import { Phone, AlertTriangle, CheckCircle, Gauge, Info } from 'lucide-react';
export function TiresWheels() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1589">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1590">
        <div className="container mx-auto px-4" data-id="element-1591">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1592">
            Authorized Cooper Tire Dealer
          </h1>
          <p className="text-xl text-gray-100" data-id="element-1593">
            Expert Tire Service, Selection & Maintenance
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1594">
        <div className="max-w-4xl mx-auto" data-id="element-1595">
          {/* Tire Pressure Information */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1596">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1597">
              <Gauge className="mr-3 text-[#B1050B]" size={32} data-id="element-1598" />
              Tire Pressure Maintenance
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6" data-id="element-1599">
              <p className="text-gray-800 mb-4" data-id="element-1600">
                It's possible for a passenger tire initially inflated to{' '}
                <strong data-id="element-1601">35 psi</strong> (pounds per square inch) to lose{' '}
                <strong data-id="element-1602">1 psi each month</strong>.
              </p>
              <p className="text-gray-800" data-id="element-1603">
                Be sure to properly check all four tires <strong data-id="element-1604">cold</strong>{' '}
                once a month and before a long trip. Whether you have a
                full-size or mini spare, make sure that it is properly inflated
                as well.
              </p>
            </div>

            <div className="space-y-4 text-gray-700" data-id="element-1605">
              <p data-id="element-1606">
                Beware of public pressure gauges at gas stations. They are often
                abused and unreliable.
              </p>
              <p data-id="element-1607">
                Purchase a good pressure gauge and check it for accuracy with
                your Michelin retailer.
              </p>
              <p className="font-semibold" data-id="element-1608">
                Check your tires <strong data-id="element-1609">cold</strong> – before you've driven
                even one mile or at least three hours after the vehicle has been
                stopped.
              </p>
            </div>
          </div>

          {/* Front End Alignment */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1610">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center" data-id="element-1611">
              Front End Alignment
            </h2>
            <p className="text-xl text-gray-700 text-center" data-id="element-1612">
              <strong data-id="element-1613">Front End Alignment should be done once a year</strong>
            </p>
          </div>

          {/* Tire Selection */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1614">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1615">
              Choose the tire you are looking for below
            </h2>
            <p className="text-gray-700 text-center mb-4" data-id="element-1616">
              Contact us to discuss your tire needs and get expert
              recommendations for your vehicle.
            </p>
            <div className="text-center" data-id="element-1617">
              <a href="tel:2484788382" className="inline-block bg-[#B1050B] hover:bg-[#8A0408] text-white px-6 py-3 rounded-lg font-semibold transition-colors" data-id="element-1618">
                Call for Tire Selection Help
              </a>
            </div>
          </div>

          {/* Tire Myths - Main Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1619">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-1620">
              The Truth About Common Tire Myths
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8" data-id="element-1621">
              <p className="text-gray-700 mb-4" data-id="element-1622">
                Bigfoot, the Abominable Snowman and the Loch Ness Monster are
                all harmless myths. But there are stories on one subject that
                are passed on from generation to generation that should be put
                to rest, according to Dave Laubie, Bridgestone/Firestone
                director of sales engineering — tire myths.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-1623">
                "It's unfortunate that so much incorrect information about tires
                and tire care still persists," Laubie said. "Tire manufacturers,
                industry associations and tire dealers have worked hard to get
                the proper information out to consumers. Unfortunately, many
                people still rely on family 'experts' for advice."
              </p>
              <p className="text-gray-700" data-id="element-1624">
                "The result of these myths is dissatisfaction with the tires,
                the tire manufacturer and the dealer that sold the tires,"
                Laubie said. "But proper tire maintenance is still the
                responsibility of the driver." Improperly maintained tires can
                lead to problems and, at the very least, a shortened tire life.
              </p>
            </div>

            <p className="text-gray-700 mb-8" data-id="element-1625">
              In an effort to separate fact from fiction, Laubie and his team of
              engineers at Bridgestone/Firestone have prepared a list of common
              tire myths, as well as their corresponding tire facts.
            </p>

            <div className="space-y-8" data-id="element-1626">
              {/* Myth 1 - Air Pressure */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1627">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1628">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1629" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1630">
                  The best air pressure for a tire is on the tire's sidewall.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1631">
                  <CheckCircle className="mr-2" size={20} data-id="element-1632" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1633">
                  The air pressure indicated on the sidewall of the tire is the
                  recommended maximum air pressure for that particular tire at a
                  specified load, not necessarily the air pressure for the tire
                  on your vehicle. Automobiles and tires are designed to work
                  together to provide the best balance of handling, traction,
                  comfort and braking characteristics. Unless you've changed
                  tire size and type, the proper air pressure for your vehicle
                  and tire combination may be found in your car's owner's
                  manual, on a sticker in the glovebox or affixed to the door
                  jamb.
                </p>
              </div>

              {/* Myth 2 - All-Season Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1634">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1635">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1636" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1637">
                  All-season radial tires are good substitutes for snow tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1638">
                  <CheckCircle className="mr-2" size={20} data-id="element-1639" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1640">
                  It depends. In general, all-season are the best tires for
                  year-round usage. However, if you live in an area that
                  receives little plowing or heavy snow, you should consider
                  switching to a snow tire during the winter.
                </p>
              </div>

              {/* Myth 3 - Spare Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1641">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1642">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1643" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1644">
                  You can drive as fast and as long on a temporary or compact
                  spare tire as you can on a regular tire.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1645">
                  <CheckCircle className="mr-2" size={20} data-id="element-1646" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1647">
                  Temporary or compact spare tires are designed to allow you to
                  get to the nearest tire dealer or service center for the
                  repair or replacement of your flat tire. Temporary spares
                  aren't designed for long-term use. The temporary spare should
                  be replaced by a regular size spare tire as soon as possible.
                  Follow your vehicle's owner's manual for proper usage of
                  temporary tires.
                </p>
              </div>

              {/* Myth 4 - Four-Wheel Drive */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1648">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1649">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1650" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1651">
                  I don't need snow tires on my four-wheel-drive vehicle.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1652">
                  <CheckCircle className="mr-2" size={20} data-id="element-1653" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1654">
                  A four-wheel-drive vehicle with all-season tires usually is
                  better in the snow than a two-wheel-drive vehicle with
                  all-seasons tires. However most vehicles will perform better
                  in the snow with snow tires rather than with all-season tires.
                  Remember: four-wheel-drive vehicles are great to get you going
                  in the snow, but they are no better than two-wheel-drive
                  vehicles at stopping or turning on snowy or icy surfaces.
                </p>
              </div>

              {/* Myth 5 - UTQG Rating */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1655">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1656">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1657" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1658">
                  The Uniform Tire Quality Grade (UTQG) rating is directly
                  related to mileage for all tire manufacturers.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1659">
                  <CheckCircle className="mr-2" size={20} data-id="element-1660" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1661">
                  The treadwear or mileage portion of UTQG is an indicator
                  within that brand of tires. It's not a good indicator between
                  brands of tires because of the broad interpretation of the
                  standard.
                </p>
              </div>

              {/* Myth 6 - Uneven Wear */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1662">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1663">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1664" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1665">
                  Uneven wear on a tire means that the tire is defective or that
                  there are broken components within the tire.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1666">
                  <CheckCircle className="mr-2" size={20} data-id="element-1667" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1668">
                  Uneven wear can result from many causes, most commonly
                  improper inflation pressure for the load being carried, lack
                  of rotation at recommended intervals and mechanical
                  conditions, such as worn or damaged suspension parts and poor
                  wheel alignment. In rare instances, it can indicate a problem
                  within the tire structure. In all cases, tires exhibiting
                  uneven wear should be examined by an authorized tire service
                  person so that the cause can be corrected.
                </p>
              </div>

              {/* Myth 7 - Vibration */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1669">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1670">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1671" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1672">
                  A vehicle ride disturbance or vibration is caused by tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1673">
                  <CheckCircle className="mr-2" size={20} data-id="element-1674" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1675">
                  A vibration felt when the vehicle is in motion is the result
                  of a rotating component being out of balance, loose or
                  excessively worn. The tires are only one of many components in
                  a vehicle. Other causes of vibration may include wheels, brake
                  rotors or drums, driveshafts, tie rods, ball joints and CV
                  joints. In the event the tire is wearing irregularly, a
                  vibration or increase in the tires' noise level may occur.
                </p>
              </div>

              {/* Myth 8 - Warranty */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1676">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1677">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1678" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1679">
                  My tire is covered by my vehicle manufacturer's warranty,
                  including punctures and cuts.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1680">
                  <CheckCircle className="mr-2" size={20} data-id="element-1681" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1682">
                  Tire manufacturers' warranties cover workmanship and materials
                  on the tires. Your vehicle's coverage does not include tires.
                  The tire manufacturers' warranties do not cover use-related
                  damages such as cuts, nail holes or impacts. However, some
                  tire retailers do sell road hazard warranties at the time of a
                  new replacement tire purchase, which can cover these types of
                  problems. These warranties generally are not offered by the
                  manufacturer, but by the individual tire dealer.
                </p>
              </div>

              {/* Myth 9 - Tire Age */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1683">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1684">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1685" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1686">
                  As long as I have tread remaining on my tires, they are safe
                  to use regardless of how old they may be.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1687">
                  <CheckCircle className="mr-2" size={20} data-id="element-1688" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1689">
                  Rubber — the main component in tires — deteriorates with age
                  and exposure to the elements, like heat sources, sunlight and
                  the ozone. It therefore virtually is impossible to put an age
                  limit on a tire. However, if the tire appears weather-cracked
                  and old, it probably is, and should be taken in for evaluation
                  by a professional.
                </p>
              </div>

              {/* Myth 10 - Blowouts */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1690">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1691">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1692" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1693">
                  My tire just blew out for no reason; it must be defective.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1694">
                  <CheckCircle className="mr-2" size={20} data-id="element-1695" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1696">
                  In most circumstances, the source of air loss is something
                  cutting, puncturing or bursting the tread or sidewall of a
                  tire.
                </p>
              </div>

              {/* Myth 11 - Mixing Tires */}
              <div className="border-l-4 border-red-500 pl-6" data-id="element-1697">
                <h3 className="text-xl font-bold text-red-700 mb-2 flex items-center" data-id="element-1698">
                  <AlertTriangle className="mr-2" size={20} data-id="element-1699" />
                  MYTH:
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1700">
                  It's acceptable to mix different types or sizes of car tires.
                </p>
                <h3 className="text-xl font-bold text-green-700 mb-2 flex items-center" data-id="element-1701">
                  <CheckCircle className="mr-2" size={20} data-id="element-1702" />
                  FACT:
                </h3>
                <p className="text-gray-700" data-id="element-1703">
                  Some combinations are acceptable; some are not. To determine
                  acceptable combinations, follow your vehicle's owner's manual
                  or consult a qualified tire service person.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 italic mt-8" data-id="element-1704">
              Source: Bridgestone
            </p>
          </div>

          {/* Tire Selection Info */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1705">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1706">
              Select Tires for my Vehicle
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-1707">
              Do you know what tire is right for your vehicle? If not, contact
              us for expert guidance.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1708">
              <p className="text-sm text-gray-700" data-id="element-1709">
                <strong data-id="element-1710">Important Note:</strong> The fitments provided by tire
                selectors have been calculated by Tire Guides On-Line. The
                information contained in the Tire Guide On-Line data software
                applies only to standard vehicles that have not been modified.
                In addition, it should be understood that while these
                applications guidelines are accurate to the best of our
                knowledge, they are only guidelines. Whenever an individual
                servicing a vehicle notices conditions that are not covered by
                the recommendations, his expertise and judgment must supersede
                these recommendations.
              </p>
            </div>
          </div>

          {/* Tire Markings Info */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1711">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1712">
              Understanding Tire Markings
            </h2>
            <p className="text-gray-700 text-center mb-6" data-id="element-1713">
              This picture explains all the markings on your tires.
            </p>
            <div className="flex justify-center mb-6" data-id="element-1714">
              <img src="https://drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/tires.jpg400x400.jpg?opt=w3" alt="Tire markings explained" className="max-w-full h-auto rounded-lg shadow-md" data-id="element-1715" />
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-1716">
              <Info className="mx-auto text-[#B1050B] mb-3" size={48} data-id="element-1717" />
              <p className="text-gray-700" data-id="element-1718">
                Ask us about tire markings and what they mean for your vehicle
                during your next visit.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1719">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1720">
              Need Tire Service or Have Questions?
            </h2>
            <p className="text-xl mb-6" data-id="element-1721">
              <strong data-id="element-1722">Any questions? Give us a call</strong>
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1723">
              <Phone className="mr-3" size={24} data-id="element-1724" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1725">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}